import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { confirmAction, notifyError, notifySuccess, notifyWarning } from '../utils/notify';

const ROLE_OPTIONS = ['Super Admin', 'Application Admin', 'Store User'];

const initialFormState = {
  id: null,
  loginId: '',
  email: '',
  password: '',
  fullName: '',
  role: 'Store User',
  isActive: true,
};

const UserMaster = () => {
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(initialFormState);

  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await api.auth.getUsers();
      if (response.success) {
        const rows = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        setUsers(rows);
      }
    } catch (error) {
      notifyError(error.message || 'Failed to fetch users');
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return users;
    }

    return users.filter((user) =>
      [user.loginId, user.fullName, user.email, user.role]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalized))
    );
  }, [users, searchTerm]);

  const openCreateModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setIsEditMode(true);
    setFormData({
      id: user.id,
      loginId: user.loginId || '',
      email: user.email || '',
      password: '',
      fullName: user.fullName || '',
      role: user.role || 'Store User',
      isActive: Boolean(user.isActive),
    });
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setFormData(initialFormState);
  };

  const openDetailsModal = (user) => {
    setSelectedUser(user);
    setIsDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setSelectedUser(null);
    setIsDetailsModalOpen(false);
  };

  const validateForm = () => {
    if (!formData.loginId || !formData.email || !formData.fullName || !formData.role) {
      notifyWarning('Please fill all mandatory fields');
      return false;
    }

    if (!isEditMode && !formData.password) {
      notifyWarning('Password is required for new users');
      return false;
    }

    if (!ROLE_OPTIONS.includes(formData.role)) {
      notifyWarning('Role must be Super Admin, Application Admin, or Store User');
      return false;
    }

    return true;
  };

  const handleSaveUser = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditMode && formData.id) {
        const payload = {
          loginId: formData.loginId,
          email: formData.email,
          fullName: formData.fullName,
          role: formData.role,
          isActive: formData.isActive,
        };

        if (formData.password) {
          payload.password = formData.password;
        }

        const response = await api.auth.updateUser(formData.id, payload);
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to update user');
        }

        notifySuccess(response.message || 'User updated successfully');
      } else {
        const response = await api.auth.createUser({
          loginId: formData.loginId,
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          role: formData.role,
          isActive: formData.isActive,
        });

        if (!response?.success) {
          throw new Error(response?.message || 'Failed to create user');
        }

        notifySuccess(response.message || 'User created successfully');
      }

      resetModal();
      await fetchUsers();
    } catch (error) {
      notifyError(error.message || 'Unable to save user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const shouldDelete = await confirmAction({
      title: 'Delete User',
      text: `Are you sure you want to delete ${user.fullName || user.loginId}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      icon: 'warning',
    });

    if (!shouldDelete) {
      return;
    }

    try {
      const response = await api.auth.deleteUser(user.id);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to delete user');
      }

      notifySuccess(response.message || 'User deleted successfully');
      await fetchUsers();
    } catch (error) {
      notifyError(error.message || 'Unable to delete user');
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return '-';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleString();
  };

  return (
    <section>
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4 md:mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold">User Management</h2>
            <p className="text-xs md:text-sm text-gray-500 mt-1">Create, update, and delete application users</p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 bg-jewel-red text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition text-sm md:text-base"
          >
            <i className="fas fa-user-plus"></i>
            <span>ADD USER</span>
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full md:w-80 p-2 border rounded text-sm md:text-base"
            placeholder="Search by login id, name, email, role"
          />
        </div>

        <div className="overflow-x-auto -mx-4 md:mx-0">
          <table className="w-full text-left border-collapse min-w-[620px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="w-32 p-2 md:p-3 border text-xs md:text-sm text-center whitespace-nowrap">Actions</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">Name</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">Login ID</th>
                <th className="p-2 md:p-3 border text-xs md:text-sm">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoadingUsers ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-sm text-gray-500">Loading users...</td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-sm text-gray-400">No users found</td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="w-32 p-2 md:p-3 border text-xs md:text-sm">
                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                        <button
                          onClick={() => openDetailsModal(user)}
                          title="Display"
                          aria-label="Display user details"
                          className="w-8 h-8 inline-flex items-center justify-center rounded border border-purple-200 text-purple-700 hover:bg-purple-50"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          onClick={() => openEditModal(user)}
                          title="Edit"
                          aria-label="Edit user"
                          className="w-8 h-8 inline-flex items-center justify-center rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          title="Delete"
                          aria-label="Delete user"
                          className="w-8 h-8 inline-flex items-center justify-center rounded border border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">{user.fullName}</td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm font-medium">{user.loginId}</td>
                    <td className="p-2 md:p-3 border text-xs md:text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg md:text-xl font-bold">{isEditMode ? 'Edit User' : 'Create User'}</h3>
              <button
                onClick={resetModal}
                className="text-gray-500 hover:text-gray-700 font-bold text-sm"
              >
                CLOSE
              </button>
            </div>

            <div className="p-4 md:p-6 bg-red-50 rounded-b-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">Login ID *</label>
                  <input
                    type="text"
                    value={formData.loginId}
                    onChange={(event) => setFormData({ ...formData, loginId: event.target.value })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                    placeholder="Enter login ID"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(event) => setFormData({ ...formData, fullName: event.target.value })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(event) => setFormData({ ...formData, email: event.target.value })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                    placeholder="user@example.com"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">Role *</label>
                  <select
                    value={formData.role}
                    onChange={(event) => setFormData({ ...formData, role: event.target.value })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">
                    Password {isEditMode ? '(optional)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                    placeholder={isEditMode ? 'Leave blank to keep existing password' : 'Enter password'}
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-bold mb-1">Status</label>
                  <select
                    value={formData.isActive ? 'true' : 'false'}
                    onChange={(event) => setFormData({ ...formData, isActive: event.target.value === 'true' })}
                    className="w-full p-2 border rounded text-sm md:text-base"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-2">
                <button
                  onClick={resetModal}
                  disabled={isSubmitting}
                  className="w-full md:w-auto bg-gray-200 text-gray-700 px-6 py-2 rounded-lg font-bold hover:bg-gray-300 transition text-sm md:text-base disabled:opacity-60"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={isSubmitting}
                  className="w-full md:w-auto bg-jewel-red text-white px-8 md:px-10 py-2 rounded-lg font-bold hover:bg-red-700 transition text-sm md:text-base disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'SAVING...' : (isEditMode ? 'UPDATE USER' : 'CREATE USER')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDetailsModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg border border-gray-200">
            <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg md:text-xl font-bold">User Details</h3>
              <button
                onClick={closeDetailsModal}
                className="text-gray-500 hover:text-gray-700 font-bold text-sm"
              >
                CLOSE
              </button>
            </div>

            <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm md:text-base">
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">Name</p>
                <p className="mt-1 font-semibold">{selectedUser.fullName || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">Login ID</p>
                <p className="mt-1 font-semibold">{selectedUser.loginId || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">Email</p>
                <p className="mt-1">{selectedUser.email || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">Role</p>
                <p className="mt-1">{selectedUser.role || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">Status</p>
                <p className="mt-1">{selectedUser.isActive ? 'Active' : 'Inactive'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-bold">Created At</p>
                <p className="mt-1">{formatDateTime(selectedUser.createdAt)}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-gray-500 uppercase font-bold">Last Login At</p>
                <p className="mt-1">{formatDateTime(selectedUser.lastLoginAt)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default UserMaster;
